/**
 * manual-quotes-page.tsx — Platform → Báo giá thủ công. Route `/platform/manual-quotes`.
 *
 * Tab "Báo giá": danh sách + lọc + thao tác (sửa, in, sao chép, đã gửi, xác nhận thanh toán, huỷ).
 * Tab "Mẫu báo giá": thông tin bên bán, tài khoản nhận tiền (VietQR), điều khoản mặc định.
 * Giá luôn lấy theo Platform → Bảng giá gói và tính ở server (cùng hàm với mua online).
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { Ban, CheckCircle2, Copy, Loader2, Pencil, Plus, Printer, Search, Send } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { platformApiError } from '@/lib/platform-client'
import { useManualQuote, useManualQuotes, useQuoteAction, useQuoteTemplate, useSaveQuoteTemplate } from '@/hooks/use-manual-quotes'
import { CancelQuoteDialog, ConfirmPaidDialog } from './manual-quote-dialogs'
import {
  fmt, ORDER_TYPE_LABELS, STATUS_LABELS, STATUS_VARIANT, viDate, type ManualQuoteListItem, type QuoteStatus, type Template,
} from './manual-quote-helpers'

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Tất cả' },
  ...(['draft', 'sent', 'expired', 'paid', 'cancelled'] as QuoteStatus[]).map((s) => ({ value: s, label: STATUS_LABELS[s] })),
]

export function ManualQuotesPage() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'template' ? 'template' : 'list'
  return (
    <div className="space-y-4">
      <PageHeader
        title="Báo giá thủ công"
        description="Lập báo giá / đơn đặt hàng cho khách theo bảng giá hiện hành; khi khách chuyển khoản, xác nhận để tạo đơn và áp gói như thanh toán trực tuyến."
        actions={<Button asChild><Link to="/platform/manual-quotes/new"><Plus className="h-4 w-4" /> Lập báo giá</Link></Button>}
      />
      <Tabs value={tab} onValueChange={(v) => setParams(v === 'template' ? { tab: 'template' } : {}, { replace: true })}>
        <TabsList>
          <TabsTrigger value="list">Báo giá</TabsTrigger>
          <TabsTrigger value="template">Mẫu báo giá</TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="mt-4"><QuoteList /></TabsContent>
        <TabsContent value="template" className="mt-4"><TemplateEditor /></TabsContent>
      </Tabs>
    </div>
  )
}

function QuoteList() {
  const navigate = useNavigate()
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useManualQuotes({ status: status || undefined, q: search || undefined, page })
  const action = useQuoteAction()
  const [paying, setPaying] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState<ManualQuoteListItem | null>(null)

  useEffect(() => {
    const t = setTimeout(() => { setSearch(q.trim()); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [q])

  const run = (row: ManualQuoteListItem, a: 'send' | 'duplicate') =>
    action.mutate({ id: row.id, action: a }, {
      onSuccess: (r) => {
        if (a === 'duplicate') { toast.success(`Đã tạo bản sao ${r.number}`); navigate(`/platform/manual-quotes/${r.id}`) }
        else toast.success('Đã đánh dấu đã gửi')
      },
      onError: (err) => toast.error(platformApiError(err)),
    })

  const open = (s: QuoteStatus) => s === 'draft' || s === 'sent' || s === 'expired'
  const columns: Column<ManualQuoteListItem>[] = [
    {
      key: 'number', header: 'Số',
      cell: (r) => (
        <div className="whitespace-nowrap">
          <Link to={`/platform/manual-quotes/${r.id}`} className="font-mono font-medium hover:text-primary">{r.number}</Link>
          <p className="text-xs text-muted-foreground">{r.orderType ? ORDER_TYPE_LABELS[r.orderType] : ''}</p>
        </div>
      ),
    },
    {
      key: 'customer', header: 'Khách hàng',
      cell: (r) => (
        <div className="min-w-0 max-w-[240px]">
          <p className="truncate font-medium" title={r.customerName}>{r.customerName || '—'}</p>
          {r.orgId ? <Link to={`/platform/companies/${r.orgId}`} className="text-xs text-primary hover:underline">Xem tổ chức</Link>
            : <p className="text-xs text-muted-foreground">Khách mới</p>}
        </div>
      ),
    },
    { key: 'plan', header: 'Gói', cell: (r) => <span className="whitespace-nowrap">{r.planName ?? '—'}{r.days ? ` · ${r.days} ngày` : ''}</span> },
    { key: 'total', header: 'Tổng (gồm VAT)', align: 'right', cell: (r) => <span className="font-medium">{fmt(r.total)} ₫</span> },
    {
      key: 'status', header: 'Trạng thái',
      cell: (r) => (
        <div className="whitespace-nowrap">
          <Badge variant={STATUS_VARIANT[r.displayStatus]}>{STATUS_LABELS[r.displayStatus]}</Badge>
          {r.dueDate && open(r.displayStatus) && <p className="mt-0.5 text-xs text-muted-foreground">Hạn {viDate(r.dueDate)}</p>}
          {r.billingOrderId && r.orgId && (
            <Link to={`/platform/companies/${r.orgId}?tab=billing`} className="block text-xs text-primary hover:underline">Xem đơn</Link>
          )}
        </div>
      ),
    },
    {
      key: 'created', header: 'Người lập',
      cell: (r) => (
        <div className="whitespace-nowrap text-xs text-muted-foreground">
          <p>{r.createdByName ?? '—'}</p>
          <p>{dayjs(r.createdAt).format('DD/MM/YYYY HH:mm')}</p>
        </div>
      ),
    },
    {
      key: 'actions', header: '', align: 'right',
      cell: (r) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {open(r.displayStatus) && (
            <Button size="icon" variant="ghost" title="Sửa" asChild><Link to={`/platform/manual-quotes/${r.id}`}><Pencil className="h-4 w-4" /></Link></Button>
          )}
          <Button size="icon" variant="ghost" title="In / PDF" asChild>
            <a href={`/platform/manual-quotes/${r.id}/print`} target="_blank" rel="noopener"><Printer className="h-4 w-4" /></a>
          </Button>
          <Button size="icon" variant="ghost" title="Sao chép thành báo giá mới" onClick={() => run(r, 'duplicate')}><Copy className="h-4 w-4" /></Button>
          {r.displayStatus === 'draft' && (
            <Button size="icon" variant="ghost" title="Đánh dấu đã gửi" onClick={() => run(r, 'send')}><Send className="h-4 w-4" /></Button>
          )}
          {open(r.displayStatus) && (
            <>
              <Button size="icon" variant="ghost" title="Xác nhận đã thanh toán" onClick={() => setPaying(r.id)}><CheckCircle2 className="h-4 w-4 text-success" /></Button>
              <Button size="icon" variant="ghost" title="Huỷ" onClick={() => setCancelling(r)}><Ban className="h-4 w-4 text-destructive" /></Button>
            </>
          )}
        </div>
      ),
    },
  ]

  if (isError) return <ErrorState message="Không tải được danh sách báo giá" />
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Số báo giá, tên khách, email, MST…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map((f) => (
            <Button key={f.value} size="sm" variant={status === f.value ? 'default' : 'outline'} onClick={() => { setStatus(f.value); setPage(1) }}>{f.label}</Button>
          ))}
        </div>
      </div>
      <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(r) => r.id} emptyTitle="Chưa có báo giá" />
      {data && data.total > data.pageSize && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Trước</Button>
          <span>Trang {page}/{totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Sau</Button>
        </div>
      )}
      {paying && <PayDialogLoader id={paying} onClose={() => setPaying(null)} />}
      {cancelling && <CancelQuoteDialog quote={cancelling} onClose={() => setCancelling(null)} />}
    </div>
  )
}

function PayDialogLoader({ id, onClose }: { id: string; onClose: () => void }) {
  const { data } = useManualQuote(id)
  if (!data) return null
  return <ConfirmPaidDialog quote={data} onClose={onClose} />
}

const TEMPLATE_FIELDS: { key: keyof Template; label: string; wide?: boolean }[] = [
  { key: 'companyName', label: 'Tên công ty bên bán', wide: true },
  { key: 'address', label: 'Địa chỉ', wide: true },
  { key: 'taxCode', label: 'Mã số thuế' },
  { key: 'phone', label: 'Hotline' },
  { key: 'email', label: 'Email' },
  { key: 'website', label: 'Website' },
  { key: 'logoUrl', label: 'Logo (đường dẫn /assets/… hoặc https://…)', wide: true },
  { key: 'signerName', label: 'Người ký bên bán' },
  { key: 'paymentMethod', label: 'Hình thức thanh toán' },
  { key: 'bankName', label: 'Ngân hàng' },
  { key: 'bankAccount', label: 'Số tài khoản' },
  { key: 'bankHolder', label: 'Chủ tài khoản' },
  { key: 'bankBin', label: 'Mã ngân hàng VietQR (MB, VCB, 970436…)' },
]

function TemplateEditor() {
  const { data, isLoading, isError } = useQuoteTemplate()
  const save = useSaveQuoteTemplate()
  const [form, setForm] = useState<Template | null>(null)
  useEffect(() => { if (data && !form) setForm(data.template) }, [data, form])
  if (isLoading || !form) return isError ? <ErrorState message="Không tải được mẫu báo giá" /> : <Loading label="Đang tải..." />
  return (
    <div className="max-w-3xl space-y-4 rounded-lg border bg-card p-4">
      <p className="text-sm text-muted-foreground">
        Mẫu dùng khi lập báo giá mới (mỗi báo giá chụp lại mẫu lúc lập). {data?.updatedAt ? `Lưu lần cuối ${dayjs(data.updatedAt).format('DD/MM/YYYY HH:mm')}.` : 'Đang dùng mặc định.'}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {TEMPLATE_FIELDS.map((f) => (
          <div key={f.key} className={f.wide ? 'space-y-1 sm:col-span-2' : 'space-y-1'}>
            <Label htmlFor={`tpl-${f.key}`}>{f.label}</Label>
            <Input id={`tpl-${f.key}`} value={String(form[f.key] ?? '')} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
          </div>
        ))}
        <div className="space-y-1">
          <Label htmlFor="tpl-due">Hạn thanh toán mặc định (ngày)</Label>
          <Input id="tpl-due" type="number" min={0} max={365} value={form.dueDays} onChange={(e) => setForm({ ...form, dueDays: Number(e.target.value) || 0 })} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="tpl-terms">Điều khoản mặc định (mỗi dòng một điều)</Label>
          <Textarea id="tpl-terms" className="min-h-[160px]" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
        </div>
      </div>
      {form.logoUrl && <img src={form.logoUrl} alt="Logo" className="h-12 w-12 rounded object-contain" />}
      <div className="flex justify-end">
        <Button
          disabled={save.isPending}
          onClick={() => save.mutate(form, {
            onSuccess: () => toast.success('Đã lưu mẫu báo giá'),
            onError: (err) => toast.error(platformApiError(err)),
          })}
        >
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Lưu mẫu
        </Button>
      </div>
    </div>
  )
}
